import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { Modal } from './modal.gts';
import { Button } from './buttons.gts';
import { eq } from '../utilities.ts';
import { PhWarning, PhTrash, PhArrowsClockwise } from 'ember-phosphor-icons';
import Alert from './alert.gts';

export interface ConfirmationModalSignature {
  Args: {
    /**
     * Controls whether the modal is visible.
     */
    isOpen: boolean;

    /**
     * The title displayed in the modal header.
     */
    title: string;

    /**
     * Visual intent of the modal. Determines header icon color.
     * - 'warning': Amber/yellow styling with warning icon
     * - 'danger': Red styling with trash/danger icon
     * @default 'warning'
     */
    intent?: 'warning' | 'danger';

    /**
     * Text for the confirm button.
     * @default 'Confirm'
     */
    confirmText?: string;

    /**
     * Text for the cancel button.
     * @default 'Cancel'
     */
    cancelText?: string;

    /**
     * If provided, user must type this exact text to enable the confirm button.
     * Useful for high-risk destructive actions.
     */
    typeToConfirm?: string;

    /**
     * Hint text shown below the type-to-confirm input.
     * @default 'Type "{typeToConfirm}" to confirm'
     */
    typeToConfirmHint?: string;

    /**
     * Shows a loading spinner on the confirm button when true.
     */
    isRunning?: boolean;

    /**
     * Error message to display in the modal body.
     */
    error?: string | null;

    /**
     * Called when the user confirms the action.
     */
    onConfirm: () => void | Promise<void>;

    /**
     * Called when the user cancels or closes the modal.
     */
    onCancel: () => void;
  };
  Blocks: {
    /**
     * The main content of the modal body.
     */
    default: [];

    /**
     * Optional warning message slot displayed above the main content.
     */
    warning?: [];
  };
  Element: HTMLDivElement;
}

/**
 * A standardized modal for confirming destructive or significant actions.
 *
 * Features:
 * - Warning (amber) and danger (red) visual variants
 * - Optional type-to-confirm for high-risk actions
 * - Loading state during async confirmation
 * - Error display
 * - Accessible with proper ARIA attributes
 *
 * @example Basic usage
 * ```gts
 * <ConfirmationModal
 *   @isOpen={{this.showModal}}
 *   @title="Delete Item"
 *   @intent="danger"
 *   @onConfirm={{this.handleDelete}}
 *   @onCancel={{this.closeModal}}
 * >
 *   Are you sure you want to delete this item?
 * </ConfirmationModal>
 * ```
 *
 * @example With type-to-confirm
 * ```gts
 * <ConfirmationModal
 *   @isOpen={{this.showModal}}
 *   @title="Rebuild Search Index"
 *   @intent="danger"
 *   @typeToConfirm="people"
 *   @onConfirm={{this.handleRebuild}}
 *   @onCancel={{this.closeModal}}
 * >
 *   This will rebuild the entire People search index.
 * </ConfirmationModal>
 * ```
 */
export default class ConfirmationModal extends Component<ConfirmationModalSignature> {
  @tracked confirmationInput = '';

  get intent() {
    return this.args.intent ?? 'warning';
  }

  get confirmText() {
    return this.args.confirmText ?? 'Confirm';
  }

  get cancelText() {
    return this.args.cancelText ?? 'Cancel';
  }

  get typeToConfirmHint() {
    if (this.args.typeToConfirmHint) {
      return this.args.typeToConfirmHint;
    }
    return `Type "${this.args.typeToConfirm}" to confirm`;
  }

  get isConfirmDisabled() {
    // Disabled while running
    if (this.args.isRunning) return true;

    // If type-to-confirm is required, check the input
    if (this.args.typeToConfirm) {
      return this.confirmationInput.trim() !== this.args.typeToConfirm;
    }

    return false;
  }

  get headerIconClass() {
    return this.intent === 'danger'
      ? 'text-red-600 dark:text-red-400'
      : 'text-amber-600 dark:text-amber-400';
  }

  get confirmButtonIntent() {
    return this.intent === 'danger' ? 'danger' : 'warning';
  }

  handleInputChange = (event: Event) => {
    this.confirmationInput = (event.target as HTMLInputElement).value;
  };

  handleConfirm = () => {
    this.args.onConfirm();
  };

  handleCancel = () => {
    this.confirmationInput = '';
    this.args.onCancel();
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.handleCancel}}
      @isCentered={{true}}
      @size="md"
      @backdrop="faded"
      data-test-confirmation-modal
      ...attributes
      as |m|
    >
      <m.Header>
        <div class="flex items-center">
          {{#if (eq this.intent "danger")}}
            <PhTrash @size={{20}} class={{this.headerIconClass}} />
          {{else}}
            <PhWarning @size={{20}} class={{this.headerIconClass}} />
          {{/if}}
          <span class="ml-2">{{@title}}</span>
        </div>
      </m.Header>
      <m.Body>
        <div class="space-y-4">
          {{#if (has-block "warning")}}
            <div
              class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4"
            >
              <div class="flex items-start">
                <PhWarning
                  @size={{20}}
                  class="text-amber-600 dark:text-amber-400 mr-2 mt-0.5 flex-shrink-0"
                />
                <div class="text-sm text-amber-800 dark:text-amber-200">
                  {{yield to="warning"}}
                </div>
              </div>
            </div>
          {{/if}}

          <div class="text-gray-700">
            {{yield}}
          </div>

          {{#if @typeToConfirm}}
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700">
                <span class="mb-1 block">{{this.typeToConfirmHint}}</span>
                <input
                  type="text"
                  value={{this.confirmationInput}}
                  {{on "input" this.handleInputChange}}
                  placeholder={{@typeToConfirm}}
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                  data-test-confirmation-input
                />
              </label>
            </div>
          {{/if}}

          {{#if @error}}
            <Alert @intent="danger" data-test-confirmation-error>
              {{@error}}
            </Alert>
          {{/if}}
        </div>
      </m.Body>
      <m.Footer>
        <div class="flex justify-end gap-3 relative">
          {{#if @isRunning}}
            <div
              class="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-900/50"
            >
              <PhArrowsClockwise @size={{24}} class="animate-spin text-gray-600" />
            </div>
          {{/if}}
          <Button
            @appearance="outlined"
            @intent="default"
            {{on "click" this.handleCancel}}
            disabled={{@isRunning}}
            data-test-confirmation-cancel
          >
            {{this.cancelText}}
          </Button>
          <Button
            @intent={{this.confirmButtonIntent}}
            {{on "click" this.handleConfirm}}
            disabled={{this.isConfirmDisabled}}
            data-test-confirmation-confirm
          >
            {{this.confirmText}}
          </Button>
        </div>
      </m.Footer>
    </Modal>
  </template>
}
